/**
 * Loyalty Settings Page – Configure loyalty program per company.
 * Min purchase to activate, rupees per point, point value, optional expiry.
 */
import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  TextField,
  Button,
  Grid,
  CircularProgress,
  Breadcrumbs,
  Link,
  Paper,
  FormHelperText,
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import SaveIcon from '@mui/icons-material/Save';
import CardGiftcardIcon from '@mui/icons-material/CardGiftcard';
import Layout from '../../components/layout/Layout';
import { useSnackbar } from 'notistack';
import loyaltySettingsService from '../../services/loyaltySettingsService';

const LoyaltySettingsPage = () => {
  const { enqueueSnackbar } = useSnackbar();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    minPurchaseAmount: '',
    amountPerPoint: '',
    pointsEarnedPerUnit: '1',
    pointValueRupees: '1',
    pointsExpiryDays: '',
  });

  const loadSettings = async () => {
    try {
      setLoading(true);
      const data = await loyaltySettingsService.get();
      setForm({
        minPurchaseAmount: data.minPurchaseAmount != null ? String(data.minPurchaseAmount) : '',
        amountPerPoint: data.amountPerPoint != null ? String(data.amountPerPoint) : '',
        pointsEarnedPerUnit: data.pointsEarnedPerUnit != null ? String(data.pointsEarnedPerUnit) : '1',
        pointValueRupees: data.pointValueRupees != null ? String(data.pointValueRupees) : '',
        pointsExpiryDays: data.pointsExpiryDays != null ? String(data.pointsExpiryDays) : '',
      });
    } catch (e) {
      enqueueSnackbar('Failed to load loyalty settings', { variant: 'error' });
      setForm({
        minPurchaseAmount: '',
        amountPerPoint: '',
        pointsEarnedPerUnit: '1',
        pointValueRupees: '',
        pointsExpiryDays: '',
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadSettings();
  }, []);

  const handleChange = (field) => (e) => {
    const v = e.target.value;
    setForm((prev) => ({ ...prev, [field]: v }));
  };

  const handleSave = async () => {
    // Treat empty fields as 0 so clearing all values = "disable loyalty"
    const parseNum = (val, def) => {
      const s = String(val || '').trim();
      if (s === '') return def;
      const n = parseFloat(s);
      return Number.isNaN(n) ? def : n;
    };
    const parseNumInt = (val, def) => {
      const s = String(val || '').trim();
      if (s === '') return def;
      const n = parseInt(s, 10);
      return Number.isNaN(n) ? def : n;
    };

    const minPurchase = parseNum(form.minPurchaseAmount, 0);
    const amountPerPoint = parseNum(form.amountPerPoint, 0);
    const pointsEarned = parseNumInt(form.pointsEarnedPerUnit, 1);
    const pointValue = parseNum(form.pointValueRupees, 0);
    const expiryDays = form.pointsExpiryDays.trim() === '' ? null : parseNumInt(form.pointsExpiryDays, null);

    if (minPurchase < 0) {
      enqueueSnackbar('Minimum purchase amount cannot be negative', { variant: 'warning' });
      return;
    }
    if (amountPerPoint < 0) {
      enqueueSnackbar('For every ₹ (amount) cannot be negative', { variant: 'warning' });
      return;
    }
    // When enabling loyalty (minPurchase > 0 or amountPerPoint > 0), require valid rules
    if (minPurchase > 0 || amountPerPoint > 0) {
      if (amountPerPoint <= 0) {
        enqueueSnackbar('For every ₹ (amount) must be greater than 0 when loyalty is enabled (e.g. 50 or 100)', { variant: 'warning' });
        return;
      }
      if (pointsEarned < 1) {
        enqueueSnackbar('Points earned must be at least 1 (e.g. 1 or 2 points for that amount)', { variant: 'warning' });
        return;
      }
      if (pointValue < 0) {
        enqueueSnackbar('Point value cannot be negative (e.g. 1, 2, or 10 ₹ per point)', { variant: 'warning' });
        return;
      }
    }
    if (expiryDays !== null && expiryDays !== undefined && (Number.isNaN(expiryDays) || expiryDays < 0)) {
      enqueueSnackbar('Points expiry days must be a non-negative number or empty', { variant: 'warning' });
      return;
    }

    try {
      setSaving(true);
      await loyaltySettingsService.save({
        minPurchaseAmount: minPurchase,
        amountPerPoint: amountPerPoint,
        pointsEarnedPerUnit: pointsEarned,
        pointValueRupees: pointValue,
        pointsExpiryDays: expiryDays,
      });
      enqueueSnackbar(
        minPurchase === 0 && amountPerPoint === 0
          ? 'Loyalty disabled. No points will be earned or redeemed.'
          : 'Loyalty settings saved successfully',
        { variant: 'success' }
      );
      loadSettings();
    } catch (e) {
      enqueueSnackbar(e.response?.data?.message || 'Failed to save loyalty settings', { variant: 'error' });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Layout>
        <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
            <CircularProgress />
          </Box>
        </Container>
      </Layout>
    );
  }

  return (
    <Layout>
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Breadcrumbs separator={<NavigateNextIcon fontSize="small" />} sx={{ mb: 3 }}>
          <Link component={RouterLink} to="/dashboard" color="inherit" underline="hover">
            Dashboard
          </Link>
          <Link component={RouterLink} to="/settings/company" color="inherit" underline="hover">
            Settings
          </Link>
          <Typography color="text.primary">Loyalty Settings</Typography>
        </Breadcrumbs>

        <Box sx={{ mb: 4 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <CardGiftcardIcon sx={{ mr: 2, fontSize: 32, color: 'primary.main' }} />
            <Typography variant="h4" component="h1">
              Loyalty Settings
            </Typography>
          </Box>
          <Typography variant="body1" color="text.secondary">
            Configure how customers earn and use loyalty points. When a bill meets the minimum purchase, points are earned based on the rules below.
          </Typography>
        </Box>

        <Paper elevation={2}>
          <CardContent sx={{ p: 4 }}>
            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CardGiftcardIcon color="primary" />
              Loyalty program rules
            </Typography>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Minimum purchase amount (₹)"
                  type="number"
                  value={form.minPurchaseAmount}
                  onChange={handleChange('minPurchaseAmount')}
                  placeholder="e.g. 200"
                  inputProps={{ min: 0, step: 1 }}
                  helperText="Bill total must be at least this amount to earn points"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="For every ₹ (amount)"
                  type="number"
                  value={form.amountPerPoint}
                  onChange={handleChange('amountPerPoint')}
                  placeholder="e.g. 50 or 100"
                  inputProps={{ min: 0, step: 1 }}
                  helperText="For every ₹X spent (e.g. 50 or 100). Set to 0 with min purchase 0 to disable loyalty."
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Points earned (for that amount)"
                  type="number"
                  value={form.pointsEarnedPerUnit}
                  onChange={handleChange('pointsEarnedPerUnit')}
                  placeholder="e.g. 1 or 2"
                  inputProps={{ min: 1, step: 1 }}
                  helperText="How many points to give (e.g. 1 pt or 2 pt for every ₹X above)"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Point value (₹ per 1 point)"
                  type="number"
                  value={form.pointValueRupees}
                  onChange={handleChange('pointValueRupees')}
                  placeholder="e.g. 1, 2, or 10"
                  inputProps={{ min: 0, step: 0.5 }}
                  helperText="1 point = ₹X when redeeming (e.g. 1, 2, or 10)"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Points expiry (days)"
                  type="number"
                  value={form.pointsExpiryDays}
                  onChange={handleChange('pointsExpiryDays')}
                  placeholder="Leave empty for no expiry"
                  inputProps={{ min: 0, step: 1 }}
                />
                <FormHelperText>Optional. Points expire after N days. Leave empty to disable expiry (for future use).</FormHelperText>
              </Grid>
              <Grid item xs={12}>
                <Button
                  variant="contained"
                  startIcon={saving ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
                  onClick={handleSave}
                  disabled={saving}
                >
                  {saving ? 'Saving...' : 'Save loyalty settings'}
                </Button>
              </Grid>
            </Grid>
          </CardContent>
        </Paper>
      </Container>
    </Layout>
  );
};

export default LoyaltySettingsPage;
